import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';
import { get } from 'lodash-es';

import { WorkflowType } from '@/constants/dispositions';
import type { FieldGroup, FormSchema } from '@/types/formSchema';
import type { AvailableField, ColumnConfig } from '@/types/table';

/**
 * Extract available fields from FieldGroup arrays (schema service).
 * This is the new API that accepts parsed field groups directly.
 * @param inputGroups - Input field groups from schema service
 * @param outputGroups - Output field groups from schema service
 */
export const extractFieldsFromSchema = (
  inputGroups: FieldGroup[],
  outputGroups: FieldGroup[],
): { inputFields: AvailableField[]; outputFields: AvailableField[] } => {
  const inputFields: AvailableField[] = [];
  const outputFields: AvailableField[] = [];

  // Extract input fields from input groups
  inputGroups.forEach(group => {
    const groupName = group.label || 'Other'; // Use group label or default to 'Other'
    if (group.fields && Array.isArray(group.fields)) {
      group.fields.forEach(field => {
        // Create a sanitized group prefix for the ID (lowercase, replace spaces with underscores)
        const groupPrefix = groupName.toLowerCase().replace(/\s+/g, '_');
        inputFields.push({
          id: `input_${groupPrefix}_${field.id}`,
          fieldPath: `input_data.data.${field.id}`,
          label: field.label,
          type: 'input',
          group: groupName,
        });
      });
    }
  });

  // Extract output fields from output groups
  outputGroups.forEach(group => {
    const groupName = group.label || 'Other'; // Use group label or default to 'Other'
    if (group.fields && Array.isArray(group.fields)) {
      group.fields.forEach(field => {
        // Create a sanitized group prefix for the ID (lowercase, replace spaces with underscores)
        const groupPrefix = groupName.toLowerCase().replace(/\s+/g, '_');
        outputFields.push({
          id: `output_${groupPrefix}_${field.id}`,
          fieldPath: `output_data.data.${field.id}`,
          label: field.label,
          type: 'output',
          group: groupName,
        });
      });
    }
  });

  return {
    inputFields,
    outputFields,
  };
};

// Extract fields from workflow definition
export const extractFieldsFromWorkflow = (
  workflow: Workflow | null,
): { inputFields: AvailableField[]; outputFields: AvailableField[] } => {
  if (!workflow?.ui) {
    return { inputFields: [], outputFields: [] };
  }

  const inputGroups = (workflow.ui.input as FormSchema)?.groups || [];
  const outputGroups = (workflow.ui.output as FormSchema)?.groups || [];

  return extractFieldsFromSchema(inputGroups, outputGroups);
};

// Save only enabled column configuration to localStorage
export const saveColumnConfig = (workflowId: string, config: ColumnConfig[]): void => {
  if (!workflowId) return;
  const key = `tableColumns_${workflowId}`;

  // Only save enabled fields (both input and output) with their id and order
  const enabledFields = config
    .filter(col => col.enabled && (col.type === 'input' || col.type === 'output'))
    .map(col => ({
      id: col.id,
      order: col.order,
    }));

  localStorage.setItem(key, JSON.stringify(enabledFields));
};

// Get default enabled columns based on workflow type
const getDefaultEnabledColumns = (workflowType: string | undefined): string[] => {
  switch (workflowType) {
    case WorkflowType.RECALL_SCHEDULER:
      return [
        'input_all_details_first_name',
        'input_all_details_mrn',
        'input_all_details_last_name',
      ];

    case WorkflowType.PBM:
      return ['input_payor_information_payor_name'];

    case WorkflowType.PQC:
      return [
        'input_patient_patient_full_name',
        'input_reporter_reporter_phone',
        'input_patient_patient_id',
        'input_event_rx_number',
        'input_metadata_case_type',
      ];

    case WorkflowType.MEDBV:
      return [];

    default:
      return [];
  }
};

// Load column preferences from localStorage with workflow type fallback
export const loadColumnPreferences = (
  workflowId: string,
  workflowType?: string,
): Array<{ id: string; order: number }> | null => {
  if (!workflowId) return null;
  const key = `tableColumns_${workflowId}`;
  const saved = localStorage.getItem(key);

  // If there are saved preferences, use them
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return parsed;
    } catch (e) {
      console.error('Failed to parse saved column config:', e);
      // Fall through to default columns
    }
  }

  // No saved preferences - use defaults based on workflow type
  const defaultColumns = getDefaultEnabledColumns(workflowType);
  if (defaultColumns.length > 0) {
    // Return default columns with their order
    return defaultColumns.map((id, index) => ({
      id,
      order: index,
    }));
  }

  return null;
};

// Get values for the first N selected input columns from case details
export const getSelectedInputFieldValues = (
  columnConfig: ColumnConfig[] | undefined,
  caseDetails: GetCaseRecordResponse | null | undefined,
  count: number = 2,
): Array<{ label: string; value: string }> => {
  if (!columnConfig || !caseDetails) return [];

  // Get the first N selected input columns
  const selectedInputFields = columnConfig
    .filter(col => col.enabled && col.type === 'input')
    .sort((a, b) => a.order - b.order) // Sort by selection order
    .slice(0, count); // Take first N columns

  // Extract values for these fields using lodash get
  return selectedInputFields.map(field => ({
    label: field.label,
    value: get(caseDetails, field.fieldPath, '--'),
  }));
};
